import React from 'react';
import { Link } from 'react-router-dom';
import {
  Package, Truck, CheckCircle, Clock, DollarSign, TrendingUp,
  Users, ArrowRight, ArrowUpRight, ArrowDownRight, Receipt,
  Calendar, AlertCircle, ChevronRight, Scan, ClipboardList,
  Wallet, UserPlus, CreditCard, FileText
} from 'lucide-react';

// Mock data for tenant dashboard
const statsCards = [
  { 
    id: 'shipments', 
    label: 'Active Shipments', 
    value: '127', 
    change: '+12%',
    trend: 'up',
    icon: Package,
    color: 'purple'
  },
  { 
    id: 'delivered', 
    label: 'Delivered (MTD)', 
    value: '342', 
    change: '+8%',
    trend: 'up',
    icon: CheckCircle, 
    color: 'lime'
  },
  { 
    id: 'pending', 
    label: 'Pending Payment', 
    value: '18', 
    change: '-5%',
    trend: 'down',
    icon: Clock, 
    color: 'amber'
  },
  { 
    id: 'revenue', 
    label: 'Revenue (MTD)', 
    value: 'JMD 2.4M', 
    change: '+23%',
    trend: 'up',
    icon: DollarSign, 
    color: 'cyan'
  },
];

const recentShipments = [
  { id: 'FS-2025-4521', customer: 'Marcus Johnson', status: 'in_transit', weight: '12.5 lbs', date: 'Dec 15', destination: 'Kingston' },
  { id: 'FS-2025-4520', customer: 'Sarah Williams', status: 'delivered', weight: '8.2 lbs', date: 'Dec 14', destination: 'Montego Bay' },
  { id: 'FS-2025-4519', customer: 'James Brown', status: 'ready', weight: '22.1 lbs', date: 'Dec 14', destination: 'Portmore' },
  { id: 'FS-2025-4518', customer: 'Emily Davis', status: 'pending', weight: '5.8 lbs', date: 'Dec 13', destination: 'Kingston' },
  { id: 'FS-2025-4517', customer: 'Michael Chen', status: 'delivered', weight: '15.3 lbs', date: 'Dec 13', destination: 'Spanish Town' },
];

const pendingInvoices = [
  { id: 'INV-FS-8521', amount: 45230, dueDate: 'Dec 30', status: 'pending' },
  { id: 'INV-FS-8498', amount: 28920, dueDate: 'Dec 25', status: 'pending' },
  { id: 'INV-FS-8476', amount: 12680, dueDate: 'Dec 20', status: 'overdue' },
];

const topCustomers = [
  { name: 'Marcus Johnson', shipments: 47, spent: 'JMD 245K' },
  { name: 'Sarah Williams', shipments: 38, spent: 'JMD 198K' },
  { name: 'James Brown', shipments: 31, spent: 'JMD 167K' },
  { name: 'Emily Davis', shipments: 28, spent: 'JMD 142K' },
];

export default function TenantDashboard() {
  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'lime';
      case 'in_transit': return 'blue';
      case 'ready': return 'cyan';
      case 'pending': return 'amber';
      case 'overdue': return 'rose';
      default: return 'gray';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'delivered': return 'Delivered';
      case 'in_transit': return 'In Transit';
      case 'ready': return 'Ready';
      case 'pending': return 'Pending';
      case 'overdue': return 'Overdue';
      default: return status;
    }
  };

  return (
    <div className="tenant-dashboard" data-testid="tenant-dashboard">
      {/* Stats Cards */}
      <div className="tenant-stats-grid">
        {statsCards.map((card) => (
          <div 
            key={card.id} 
            className={`tenant-stat-card ${card.color}`}
            data-testid={`stat-${card.id}`}
          >
            <div className="stat-card-header">
              <div className={`stat-icon ${card.color}`}>
                <card.icon size={20} />
              </div>
              <div className={`stat-change ${card.trend}`}>
                {card.trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {card.change}
              </div>
            </div>
            <div className="stat-card-body">
              <span className="stat-value">{card.value}</span>
              <span className="stat-label">{card.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Main Grid */}
      <div className="tenant-dashboard-grid">
        {/* Recent Shipments */}
        <div className="dashboard-card shipments-card">
          <div className="card-header">
            <h3>Recent Shipments</h3>
            <Link to="/tenant/shipments" className="view-all-link" data-testid="view-all-shipments">
              View All <ChevronRight size={16} />
            </Link>
          </div>
          <div className="shipments-table">
            <div className="table-header">
              <span>Tracking #</span>
              <span>Customer</span>
              <span>Status</span>
              <span>Weight</span>
              <span>Destination</span>
            </div>
            {recentShipments.map((shipment) => (
              <div key={shipment.id} className="table-row" data-testid={`shipment-${shipment.id}`}>
                <span className="tracking-id">{shipment.id}</span>
                <span className="customer-name">{shipment.customer}</span>
                <span className={`status-badge ${getStatusColor(shipment.status)}`}>
                  {getStatusLabel(shipment.status)}
                </span>
                <span className="weight">{shipment.weight}</span>
                <span className="destination">{shipment.destination}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column */}
        <div className="dashboard-right-column">
          {/* Pending Invoices */}
          <div className="dashboard-card invoices-card">
            <div className="card-header">
              <h3>Pending Invoices</h3>
              <Link to="/tenant/invoices" className="view-all-link" data-testid="view-all-invoices">
                View All
              </Link>
            </div>
            <div className="pending-invoices-list">
              {pendingInvoices.map((invoice) => (
                <div key={invoice.id} className="pending-invoice-item" data-testid={`invoice-${invoice.id}`}>
                  <div className="invoice-info">
                    <Receipt size={16} />
                    <div>
                      <span className="invoice-id">{invoice.id}</span>
                      <span className="invoice-due">Due: {invoice.dueDate}</span>
                    </div>
                  </div>
                  <div className="invoice-amount-section">
                    <span className="invoice-amount">JMD {invoice.amount.toLocaleString()}</span>
                    {invoice.status === 'overdue' && (
                      <span className="overdue-badge">
                        <AlertCircle size={12} /> Overdue
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="card-footer">
              <div className="total-pending">
                <span>Total Pending</span>
                <span className="total-amount">JMD {(45230 + 28920 + 12680).toLocaleString()}</span>
              </div>
              <button className="btn btn-primary btn-sm" data-testid="pay-all-btn">
                Pay All <ArrowRight size={14} />
              </button>
            </div>
          </div>

          {/* Top Customers */}
          <div className="dashboard-card customers-card">
            <div className="card-header">
              <h3>Top Customers</h3>
              <Link to="/tenant/customers" className="view-all-link" data-testid="view-all-customers">
                View All
              </Link>
            </div>
            <div className="top-customers-list">
              {topCustomers.map((customer, index) => (
                <div key={index} className="customer-item" data-testid={`customer-${index}`}>
                  <div className="customer-rank">{index + 1}</div>
                  <div className="customer-info">
                    <span className="customer-name">{customer.name}</span>
                    <span className="customer-stats">{customer.shipments} shipments</span>
                  </div>
                  <span className="customer-spent">{customer.spent}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions - Enhanced */}
      <div className="quick-actions-section enhanced">
        <div className="section-header">
          <h3>Quick Actions</h3>
          <span className="section-subtitle">Common operations</span>
        </div>
        <div className="quick-actions-grid enhanced">
          <Link to="/tenant/delivery-board" className="quick-action-card primary" data-testid="action-start-delivery">
            <div className="action-icon">
              <Truck size={24} />
            </div>
            <div className="action-content">
              <span className="action-title">Start Delivery</span>
              <span className="action-desc">Manage delivery routes</span>
            </div>
            <ChevronRight size={18} className="action-arrow" />
          </Link>
          <Link to="/tenant/invoices" className="quick-action-card" data-testid="action-create-invoice">
            <div className="action-icon">
              <Receipt size={24} />
            </div>
            <div className="action-content">
              <span className="action-title">Create Invoice</span>
              <span className="action-desc">Bill your customers</span>
            </div>
            <ChevronRight size={18} className="action-arrow" />
          </Link>
          <Link to="/tenant/clients" className="quick-action-card" data-testid="action-add-client">
            <div className="action-icon">
              <UserPlus size={24} />
            </div>
            <div className="action-content">
              <span className="action-title">Add Client</span>
              <span className="action-desc">Register new customer</span>
            </div>
            <ChevronRight size={18} className="action-arrow" />
          </Link>
          <Link to="/tenant/finances" className="quick-action-card" data-testid="action-record-payment">
            <div className="action-icon">
              <CreditCard size={24} />
            </div>
            <div className="action-content">
              <span className="action-title">Record Payment</span>
              <span className="action-desc">Log received payments</span>
            </div>
            <ChevronRight size={18} className="action-arrow" />
          </Link>
          <Link to="/tenant/intake" className="quick-action-card" data-testid="action-intake-scan">
            <div className="action-icon">
              <Scan size={24} />
            </div>
            <div className="action-content">
              <span className="action-title">Intake Scan</span>
              <span className="action-desc">Scan arriving packages</span>
            </div>
            <ChevronRight size={18} className="action-arrow" />
          </Link>
          <Link to="/tenant/bom" className="quick-action-card" data-testid="action-create-bom">
            <div className="action-icon">
              <FileText size={24} />
            </div>
            <div className="action-content">
              <span className="action-title">Create BOM</span>
              <span className="action-desc">New operational manifest</span>
            </div>
            <ChevronRight size={18} className="action-arrow" />
          </Link>
        </div>
      </div>
    </div>
  );
}
